import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Zap, Hand, Plus, RefreshCw, Trash2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UserTag } from "@/hooks/useUserTags";
import { useTagAutoRules, type TagAutoRule, type RuleFilter } from "@/hooks/useTagAutoRules";
import { TagRuleForm } from "./TagRuleForm";
import { formatDistanceToNow } from "date-fns";

const AVAILABLE_COLORS = [
  "purple", "pink", "green", "emerald", "blue", "amber", "teal",
  "indigo", "violet", "gray", "red", "orange", "cyan", "rose",
];

// Auto-assign color based on name hash
const getAutoColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVAILABLE_COLORS[Math.abs(hash) % AVAILABLE_COLORS.length];
};

const FIELD_LABELS: Record<string, string> = {
  events_count: "Events",
  inactive_days: "Inactive Days",
  credits_balance: "Credits",
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email",
  phone: "Phone",
  member_type: "Member Type",
  role: "Role",
  is_unlimited: "Unlimited",
  referral_code: "Referral Code",
  days_since_joined: "Days Since Joined",
  days_since_last_active: "Days Since Last Active",
  days_since_last_purchase: "Days Since Last Purchase",
};

const OPERATOR_LABELS: Record<string, string> = {
  "=": "=",
  "<>": "≠",
  ">": ">",
  "<": "<",
  ">=": "≥",
  "<=": "≤",
  "~~": "~",
  "~~*": "~*",
  "in": "∈",
  "is": "is",
  "len>": "len >",
  "len<": "len <",
  "len>=": "len ≥",
  "len<=": "len ≤",
  "len=": "len =",
};

const FIELD_COLORS: Record<string, string> = {
  events_count: "bg-blue-500/20 text-blue-700 border-blue-500/30",
  inactive_days: "bg-orange-500/20 text-orange-700 border-orange-500/30",
  credits_balance: "bg-green-500/20 text-green-700 border-green-500/30",
  first_name: "bg-slate-500/20 text-slate-700 border-slate-500/30",
  last_name: "bg-slate-500/20 text-slate-700 border-slate-500/30",
  email: "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
  phone: "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
  member_type: "bg-purple-500/20 text-purple-700 border-purple-500/30",
  role: "bg-red-500/20 text-red-700 border-red-500/30",
  is_unlimited: "bg-amber-500/20 text-amber-700 border-amber-500/30",
  referral_code: "bg-teal-500/20 text-teal-700 border-teal-500/30",
  days_since_joined: "bg-indigo-500/20 text-indigo-700 border-indigo-500/30",
  days_since_last_active: "bg-emerald-500/20 text-emerald-700 border-emerald-500/30",
  days_since_last_purchase: "bg-pink-500/20 text-pink-700 border-pink-500/30",
};

interface TagFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tag?: UserTag | null;
  onSubmit: (data: { name: string; color: string; description: string; tag_type: 'dynamic' | 'manual' }) => Promise<void>;
  isSubmitting: boolean;
}

export function TagFormDialog({
  open,
  onOpenChange,
  tag,
  onSubmit,
  isSubmitting,
}: TagFormDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tagType, setTagType] = useState<'dynamic' | 'manual'>('manual');
  
  // Filter management state
  const [showFilterForm, setShowFilterForm] = useState(false);
  const [editingRule, setEditingRule] = useState<TagAutoRule | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const [syncingRuleId, setSyncingRuleId] = useState<string | null>(null);
  const [filterFormKey, setFilterFormKey] = useState(0);

  const isEditing = !!tag;
  const isDynamicTag = isEditing && tag?.tag_type === 'dynamic';

  // Only fetch rules when editing a dynamic tag
  const {
    rules,
    isLoading: isLoadingRules,
    createRule,
    updateRule,
    deleteRule,
    syncRule,
    syncAllRules,
    isCreating,
    isUpdating,
    isDeleting,
    isSyncing,
  } = useTagAutoRules(isDynamicTag ? tag.id : undefined);

  useEffect(() => {
    if (open) {
      if (tag) {
        setName(tag.name);
        setDescription(tag.description || "");
        setTagType(tag.tag_type || 'manual');
      } else {
        setName("");
        setDescription("");
        setTagType("manual");
      }
      // Reset filter form state when dialog opens
      setShowFilterForm(false);
      setEditingRule(null);
    }
  }, [open, tag]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    // Auto-assign color based on name, or keep existing color when editing
    const color = isEditing ? (tag?.color || getAutoColor(name.trim())) : getAutoColor(name.trim());
    await onSubmit({ name: name.trim(), color, description: description.trim(), tag_type: tagType });
  };

  // Filter handlers
  const handleCreateFilter = async (filters: RuleFilter[]) => {
    if (!tag) return;
    await createRule({ tagId: tag.id, filters });
    // Reset form for the next filter by incrementing key
    setFilterFormKey((k) => k + 1);
  };

  const handleUpdateFilter = async (filters: RuleFilter[]) => {
    if (!editingRule) return;
    await updateRule({ ruleId: editingRule.id, filters });
    setEditingRule(null);
  };

  const handleToggleActive = async (rule: TagAutoRule) => {
    await updateRule({ ruleId: rule.id, isActive: !rule.is_active });
  };

  const handleDeleteFilter = async () => {
    if (!deletingRuleId) return;
    await deleteRule(deletingRuleId);
    setDeletingRuleId(null);
  };

  const handleSyncFilter = async (ruleId: string) => {
    setSyncingRuleId(ruleId);
    try {
      await syncRule(ruleId);
    } finally {
      setSyncingRuleId(null);
    }
  };

  const handleSyncAll = async () => {
    if (!tag) return;
    await syncAllRules(tag.id);
  };

  const renderFilterCard = (rule: TagAutoRule) => {
    const filters = rule.filters;
    const isSyncingThis = syncingRuleId === rule.id;

    return (
      <div
        key={rule.id}
        className={cn(
          "border rounded-lg p-3 space-y-2",
          !rule.is_active && "opacity-60 bg-muted/30"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              checked={rule.is_active}
              onCheckedChange={() => handleToggleActive(rule)}
              disabled={isUpdating}
            />
            <span className="text-sm font-medium">
              {rule.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setEditingRule(rule)}
              disabled={isUpdating}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => handleSyncFilter(rule.id)}
              disabled={isSyncing}
            >
              {isSyncingThis ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={() => setDeletingRuleId(rule.id)}
              disabled={isDeleting}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {filters.map((filter, idx) => (
            <Badge
              key={idx}
              variant="outline"
              className={cn("text-xs", FIELD_COLORS[filter.field] || "")}
            >
              {FIELD_LABELS[filter.field] || filter.field} {OPERATOR_LABELS[filter.operator] || filter.operator} {filter.value}
            </Badge>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {rule.users_matched} user{rule.users_matched !== 1 ? "s" : ""} matched
          </span>
          <span>
            {rule.last_synced_at
              ? `Synced ${formatDistanceToNow(new Date(rule.last_synced_at))} ago`
              : "Never synced"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn("sm:max-w-md", isDynamicTag && "sm:max-w-lg max-h-[85vh] overflow-y-auto")}>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Tag" : "Create Tag"}</DialogTitle>
            <DialogDescription>
              {isEditing 
                ? "Update the tag details below." 
                : "Create a new tag to organize and segment users."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Name *</Label>
              <Input
                id="tag-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., VIP, Early Adopter"
                maxLength={50}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Tag Type - only shown when creating, not editable */}
            {!isEditing && (
              <div className="space-y-2">
                <Label>Tag Type *</Label>
                <RadioGroup
                  value={tagType}
                  onValueChange={(value) => setTagType(value as 'dynamic' | 'manual')}
                  className="grid grid-cols-2 gap-3"
                  disabled={isSubmitting}
                >
                  <Label
                    htmlFor="tag-type-dynamic"
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                      tagType === 'dynamic' 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:bg-muted/50"
                    )}
                  >
                    <RadioGroupItem value="dynamic" id="tag-type-dynamic" className="mt-0.5" />
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 font-medium">
                        <Zap className="h-3.5 w-3.5" />
                        Dynamic
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Auto-assigned based on filters
                      </p>
                    </div>
                  </Label>
                  <Label
                    htmlFor="tag-type-manual"
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                      tagType === 'manual' 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:bg-muted/50"
                    )}
                  >
                    <RadioGroupItem value="manual" id="tag-type-manual" className="mt-0.5" />
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5 font-medium">
                        <Hand className="h-3.5 w-3.5" />
                        Manual
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Manually assigned to users
                      </p>
                    </div>
                  </Label>
                </RadioGroup>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tag-description">Description</Label>
              <Textarea
                id="tag-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description for this tag..."
                maxLength={200}
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Filters section for dynamic tags when editing */}
            {isDynamicTag && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Auto-assignment Filters</Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Conditions within a filter use AND. Multiple filters use OR.
                      </p>
                    </div>
                    {rules.length > 0 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleSyncAll}
                        disabled={isSyncing}
                      >
                        {isSyncing ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4 mr-1" />
                        )}
                        Sync All
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {isLoadingRules ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        Loading filters...
                      </div>
                    ) : rules.length === 0 && !showFilterForm ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        No filters yet. Add one to automatically assign this tag.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {rules.map(renderFilterCard)}
                      </div>
                    )}

                    {/* Inline filter form - shown below existing filters */}
                    {(showFilterForm || editingRule) && (
                      <div className="border rounded-lg p-3 bg-muted/30">
                        <div className="text-sm font-medium mb-2 text-muted-foreground">
                          {editingRule ? "Edit Filter" : "New Filter"}
                        </div>
                        <TagRuleForm
                          key={editingRule ? editingRule.id : filterFormKey}
                          initialFilters={editingRule?.filters}
                          onSubmit={editingRule ? handleUpdateFilter : handleCreateFilter}
                          onCancel={() => {
                            setShowFilterForm(false);
                            setEditingRule(null);
                          }}
                          isSubmitting={isCreating || isUpdating}
                        />
                      </div>
                    )}

                    {/* Add filter button - only shown when not adding/editing */}
                    {!showFilterForm && !editingRule && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFilterForm(true)}
                        className="w-full border-dashed"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Filter
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Always show footer - filter form has its own inline buttons */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !name.trim()}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? "Save Changes" : "Create Tag"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete filter confirmation */}
      <AlertDialog
        open={!!deletingRuleId}
        onOpenChange={() => setDeletingRuleId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Filter</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this filter? This won't
              remove existing tag assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFilter}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
